import React, { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import {
  PollModificationTracker,
  type PollModificationStats,
} from "../../services/PollModificationTracker";

/**
 * KPI card pour le dashboard admin : volume de modifications AI sur 24h /
 * 7j / 30j. Sert à mesurer le coût Gemini consommé par le flow
 * `editPollFromText` (chaque appel = 1 modif facturée). Plafond UI par
 * sondage : MAX_MODIFICATIONS_PER_POLL (cf. constants/quotas.ts).
 *
 * Cf. spec : Docs/SPECS/2026-05-12-spec-modifications-proposition-sondage.md
 */
export const PollModificationsKPI: React.FC = () => {
  const [stats, setStats] = useState<PollModificationStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    PollModificationTracker.getStats()
      .then((s) => {
        if (!cancelled) setStats(s);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-400" />
          Modifications IA (date polls)
        </h3>
      </div>

      {loading ? (
        <div className="text-white/60 text-sm">Chargement…</div>
      ) : stats ? (
        <div className="grid grid-cols-3 gap-4">
          <Bucket label="Dernières 24h" value={stats.countLast24h} />
          <Bucket label="7 derniers jours" value={stats.countLast7d} />
          <Bucket label="30 derniers jours" value={stats.countLast30d} />
        </div>
      ) : (
        <div className="text-white/60 text-sm">Pas de données.</div>
      )}

      <p className="mt-4 text-xs text-white/40">
        Chaque appel équivaut à un message Gemini facturé. Plafond par sondage
        côté UI configuré via{" "}
        <code className="text-white/60">MAX_MODIFICATIONS_PER_POLL</code>.
      </p>
    </div>
  );
};

interface BucketProps {
  label: string;
  value: number;
}

const Bucket: React.FC<BucketProps> = ({ label, value }) => (
  <div>
    <div className="text-2xl font-bold text-white">{value}</div>
    <div className="text-xs text-white/60">{label}</div>
  </div>
);
